import { z } from "zod";
import { LOOK_ALIKE_REASONS } from "./supplier-names";

/**
 * Sourcing analysis (WP-3.2, ADR-0027): what a buyer sees on the review page — advisory
 * make-or-buy, the supplier the requester named, a panel shortlist scored by code, a spend
 * benchmark over purchase history, and a narrative (task `sourcing_narrative`) checked by code.
 * Internal: never in a requester payload (domain rule 7).
 */

export const PANEL_SENIORITIES = ["JUNIOR", "MIDDLE", "SENIOR", "FLAT"] as const;
export type PanelSeniority = (typeof PANEL_SENIORITIES)[number];
export const PANEL_CONTRACT_TYPES = ["RATE", "VALUE"] as const;
export type PanelContractType = (typeof PANEL_CONTRACT_TYPES)[number];
export const PANEL_ENTITY_TYPES = ["COMPANY", "INDIVIDUAL"] as const;
export type PanelEntityType = (typeof PANEL_ENTITY_TYPES)[number];

/**
 * Role areas of the vendor panel (its "Professional role/area of expertise" column), as stable keys.
 * `other` is any panel area this list does not know; it is never matched by a request.
 */
export const ROLE_AREAS = [
  "software_development",
  "project_management",
  "functional_analysis",
  "consulting",
  "architecture",
  "ux_ui",
  "data_analysis",
  "testing",
  "ai_bi",
  "integration",
  "networking",
  "maintenance",
  "cyber_privacy",
  "delivery",
  "dba",
  "agile",
  "training",
  "data_entry",
  "other",
] as const;
export type RoleArea = (typeof ROLE_AREAS)[number];

// ───────────── Make-or-buy (advisory, ADR-0027 §2) ─────────────

export const MAKE_OR_BUY_REASONS = [
  "capacity_only",
  "core_recurring",
  "large_volume",
  "strategic_bridge",
  "high_risk_strategic",
  "large_volume_capability_gap",
  "tactical",
] as const;
export type MakeOrBuyReason = (typeof MAKE_OR_BUY_REASONS)[number];

/** Registry fields the rules read; listed in `missingInputs` when not captured. */
export const MAKE_OR_BUY_INPUTS = [
  "is_only_capacity",
  "competence_type",
  "is_recurring",
  "strategic_classification",
  "expertise_type",
  "nature_of_work",
  "deliverables",
  "resources_needed",
  "estimated_duration",
] as const;

export const makeOrBuySchema = z.object({
  recommendation: z.enum(["INTERNAL", "EXTERNAL"]),
  bridgeVendor: z.boolean(),
  /** 0–1, two decimals. */
  strategicIndex: z.number(),
  riskIndex: z.number(),
  reasons: z.array(
    z.object({
      code: z.enum(MAKE_OR_BUY_REASONS),
      resources: z.number().nullable(),
      months: z.number().nullable(),
    }),
  ),
  missingInputs: z.array(z.string()),
});
export type MakeOrBuy = z.infer<typeof makeOrBuySchema>;

// ───────────── Named supplier (ADR-0027 §3) ─────────────

export const NAMED_SUPPLIER_STATUSES = ["none", "on_panel", "possible_match", "history_only", "unknown"] as const;
export type NamedSupplierStatus = (typeof NAMED_SUPPLIER_STATUSES)[number];

export const namedSupplierSchema = z.object({
  status: z.enum(NAMED_SUPPLIER_STATUSES),
  /** As the request records it; null when no supplier was named. */
  name: z.string().nullable(),
  supplierId: z.uuid().nullable(),
  displayName: z.string().nullable(),
  orderCount: z.number().int(),
  lastOrderDate: z.string().nullable(),
  /** For `possible_match`: why the names look alike (ADR-0026 rule). */
  possibleReason: z.enum(LOOK_ALIKE_REASONS).nullable(),
});
export type NamedSupplier = z.infer<typeof namedSupplierSchema>;

// ───────────── Shortlist (code, ADR-0027 §4) ─────────────

export const rateRangeSchema = z.object({
  roleArea: z.enum(ROLE_AREAS),
  /** The panel's own wording of the role area. */
  roleLabel: z.string(),
  seniority: z.enum(PANEL_SENIORITIES).nullable(),
  contractType: z.enum(PANEL_CONTRACT_TYPES),
  /** EUR per person-day; null for a fixed-value contract or a rate the panel does not give. */
  min: z.number().nullable(),
  max: z.number().nullable(),
});
export type RateRange = z.infer<typeof rateRangeSchema>;

export const candidateSchema = z.object({
  supplierId: z.uuid(),
  displayName: z.string(),
  entityType: z.enum(PANEL_ENTITY_TYPES),
  /** 0–1: role-area coverage .7, seniority fit .3. */
  score: z.number(),
  roleAreasCovered: z.array(z.enum(ROLE_AREAS)),
  seniorityFit: z.boolean(),
  /** The supplier the requester named. */
  named: z.boolean(),
  rates: z.array(rateRangeSchema),
  /** The panel's Activity / Activity + Notes text for the matching rows (what criteria can be evidenced from). */
  activities: z.array(z.string()),
  /** Shown beside the candidate, never scored: familiarity is not fit. */
  history: z.object({ orders: z.number().int(), lastOrderDate: z.string().nullable() }),
});
export type Candidate = z.infer<typeof candidateSchema>;

export const SHORTLIST_OUTCOMES = ["candidates", "no_role_area", "no_panel_supplier", "no_panel"] as const;
export type ShortlistOutcome = (typeof SHORTLIST_OUTCOMES)[number];
/** Candidates kept (the named supplier comes on top of these). */
export const SHORTLIST_SIZE = 5;

export const shortlistSchema = z.object({
  outcome: z.enum(SHORTLIST_OUTCOMES),
  roleAreas: z.array(z.enum(ROLE_AREAS)),
  seniority: z.array(z.enum(["JUNIOR", "MIDDLE", "SENIOR"])),
  candidates: z.array(candidateSchema),
  /** Panel suppliers in the role areas left out because they are individuals (ADR-0027). */
  individualsExcluded: z.number().int(),
  /** Further companies covering the role areas beyond the shortlist size: the cut-off is never silent. */
  alsoMatching: z.number().int(),
});
export type Shortlist = z.infer<typeof shortlistSchema>;

// ───────────── Spend benchmark (code, ADR-0027 §5) ─────────────

/** Fewer comparable orders than this is no benchmark. */
export const BENCHMARK_MIN_ORDERS = 3;

export const benchmarkScopeSchema = z.object({
  scope: z.enum(["category", "supplier"]),
  /** Category names joined, or the supplier's name. */
  label: z.string(),
  supplierId: z.uuid().nullable(),
  orders: z.number().int(),
  suppliers: z.number().int(),
  firstOrderDate: z.string().nullable(),
  lastOrderDate: z.string().nullable(),
  /** EUR, whole euros. Null when there are no orders. */
  min: z.number().nullable(),
  p25: z.number().nullable(),
  median: z.number().nullable(),
  p75: z.number().nullable(),
  max: z.number().nullable(),
  /** Orders at or above the request's budget; null when no budget is known. */
  atOrAboveBudget: z.number().int().nullable(),
  /** At least BENCHMARK_MIN_ORDERS orders. */
  enough: z.boolean(),
});
export type BenchmarkScope = z.infer<typeof benchmarkScopeSchema>;

export const benchmarkSchema = z.object({
  categories: z.array(z.string()),
  /** EUR budget the category scope is banded to (0.25×–4×); null when no budget parses. */
  budget: z.number().nullable(),
  budgetApproved: z.boolean(),
  band: z.object({ min: z.number(), max: z.number() }).nullable(),
  category: benchmarkScopeSchema.nullable(),
  suppliers: z.array(benchmarkScopeSchema),
  /** Candidates (and the named supplier) with no order at all: named, never dropped. */
  withoutHistory: z.array(z.object({ supplierId: z.uuid(), displayName: z.string() })),
  currency: z.literal("EUR"),
});
export type Benchmark = z.infer<typeof benchmarkSchema>;

// ───────────── Narrative (task `sourcing_narrative`, ADR-0027 §6) ─────────────

export const SUPPLIER_LABELS = ["candidate", "shortlisted"] as const;

export const sourcingNarrativeSchema = z.object({
  mandatoryCriteria: z
    .array(z.string().min(1).max(200))
    .max(8)
    .describe("The capabilities this request actually requires, specific (not 'consulting' but 'SAP FI rollout')."),
  suppliers: z
    .array(
      z.object({
        supplierId: z.string().describe("The id of a supplier from <candidates>, exactly."),
        label: z
          .enum(SUPPLIER_LABELS)
          .describe("shortlisted only when every criterion checkable from the panel data is met; else candidate."),
        criteriaMet: z.array(z.string()).describe("Mandatory criteria the panel data evidences, copied exactly."),
        criteriaUnverified: z.array(z.string()).describe("Mandatory criteria the panel data does not evidence."),
        whyInvite: z
          .string()
          .max(600)
          .describe("Why this supplier is worth inviting — never that it should be chosen."),
      }),
    )
    .max(8),
  panelSummary: z.string().max(1200).describe("What the panel offers for this request, including gaps."),
  commercialNote: z.string().max(1200).describe("Rates with their units and the benchmark, from the figures given."),
  nextSteps: z.string().max(800).describe("What the buyer should do next; never names a chosen supplier."),
});
export type SourcingNarrative = z.infer<typeof sourcingNarrativeSchema>;

export const NARRATIVE_PROBLEMS = [
  "unknown_supplier",
  "duplicate_supplier",
  "unknown_criterion",
  "recommendation_wording",
  "unsupported_figure",
  "budget_wording",
] as const;
export type NarrativeProblem = (typeof NARRATIVE_PROBLEMS)[number];

/** Words that turn an invitation into a selection (IT/EN). The legacy review's core failure. */
const SELECTION_WORDS =
  /\b(recommend(?:ed|s|ation)?|preferred|best|top choice|winner|raccomand\w*|consigliat\w*|migliore|preferit\w*|scelta ideale)\b/i;
/** Wording that presents a budget as approved. */
const APPROVED_BUDGET =
  /\b(approved budget|budget (?:is |has been )?approved|budget approvato|budget (?:è stato )?approvato)\b/i;
/** Euro amounts written in prose: €1.200, 1,200 EUR, 450 euro, €45k. */
const EURO_FIGURE = /(?:€\s*(\d[\d.,]*)\s*(k|mila)?|(\d[\d.,]*)\s*(k|mila)?\s*(?:€|eur\b|euro\b))/gi;

function figureValue(digits: string, suffix: string | undefined): number | null {
  const cleaned = digits.replace(/[.,](?=\d{3}(?!\d))/g, "").replace(",", ".");
  const value = Number.parseFloat(cleaned);
  if (!Number.isFinite(value)) return null;
  return suffix ? value * 1000 : value;
}

/** Euro figures written in a text, as numbers. */
export function euroFiguresIn(text: string): number[] {
  const out: number[] = [];
  for (const m of text.matchAll(EURO_FIGURE)) {
    const v = m[1] !== undefined ? figureValue(m[1], m[2]) : m[3] !== undefined ? figureValue(m[3], m[4]) : null;
    if (v !== null) out.push(v);
  }
  return out;
}

export interface NarrativeCheckInput {
  supplierIds: string[];
  /** Every euro figure the input gave the model (rates, benchmark figures, budget). */
  figures: number[];
  budgetApproved: boolean;
}

/**
 * Code's checks on the model's narrative (ADR-0027 §6). Empty = acceptable. A figure counts as
 * supported when it is within 1 % (or €1) of a figure the model was given — rounding, not invention.
 */
export function narrativeProblems(n: SourcingNarrative, input: NarrativeCheckInput): NarrativeProblem[] {
  const problems = new Set<NarrativeProblem>();
  const known = new Set(input.supplierIds);
  const seen = new Set<string>();
  const criteria = new Set(n.mandatoryCriteria);
  for (const s of n.suppliers) {
    if (!known.has(s.supplierId)) problems.add("unknown_supplier");
    if (seen.has(s.supplierId)) problems.add("duplicate_supplier");
    seen.add(s.supplierId);
    if ([...s.criteriaMet, ...s.criteriaUnverified].some((c) => !criteria.has(c))) problems.add("unknown_criterion");
  }
  const prose = [n.panelSummary, n.commercialNote, n.nextSteps, ...n.suppliers.map((s) => s.whyInvite)];
  if (prose.some((p) => SELECTION_WORDS.test(p))) problems.add("recommendation_wording");
  if (!input.budgetApproved && prose.some((p) => APPROVED_BUDGET.test(p))) problems.add("budget_wording");
  const supported = (v: number) => input.figures.some((f) => Math.abs(f - v) <= Math.max(1, Math.abs(f) * 0.01));
  if (prose.some((p) => euroFiguresIn(p).some((v) => !supported(v)))) problems.add("unsupported_figure");
  return [...problems];
}

// ───────────── The analysis ─────────────

export const SOURCING_STATUSES = ["PENDING", "READY", "FAILED"] as const;
export type SourcingStatus = (typeof SOURCING_STATUSES)[number];

export const sourcingAnalysisSchema = z.object({
  requestId: z.uuid(),
  status: z.enum(SOURCING_STATUSES),
  failureReason: z.string().nullable(),
  /** When the current content was built; null before the first build finishes. */
  builtAt: z.string().nullable(),
  /** The request changed since the analysis was built (a refresh would differ). */
  stale: z.boolean(),
  makeOrBuy: makeOrBuySchema.nullable(),
  namedSupplier: namedSupplierSchema.nullable(),
  shortlist: shortlistSchema.nullable(),
  benchmark: benchmarkSchema.nullable(),
  narrative: sourcingNarrativeSchema.nullable(),
  /** Why the narrative is missing (model failed or failed code's checks twice). */
  narrativeProblems: z.array(z.string()),
  panelImportedAt: z.string().nullable(),
  historyImportedAt: z.string().nullable(),
});
export type SourcingAnalysis = z.infer<typeof sourcingAnalysisSchema>;

/** GET /api/requests/:id/sourcing — null until the first analysis is requested. */
export const sourcingResponseSchema = z.object({ analysis: sourcingAnalysisSchema.nullable() });
export type SourcingResponse = z.infer<typeof sourcingResponseSchema>;
